import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "handyman-web-design-colorado",
  metaTitle: "Handyman Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom handyman web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Handyman Web Design Colorado",
  eyebrow: "Handyman Web Design — Colorado",
  h1: (
    <>
      Handyman <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded handyman websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Handyman websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first handyman sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Handyman web design in Colorado",
    body: "Handyman web design is the practice of building conversion-focused, mobile-first websites for handyman businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — handyman calls are same-day intent." },
    { title: "Task-based service pages", body: "Drywall, painting, fixture install, TV mount, assembly, deck repair — each ranks independently." },
    { title: "Instant quote + booking", body: "Hourly + flat-rate calculators that book the visit in under 60 seconds." },
    { title: "Sticky click-to-call", body: "Phone visible on every scroll, every page." },
    { title: "Trust signals above the fold", body: "Insurance, bonding, and Google reviews surfaced where buyers actually look." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Task-based searches dominate", body: "\"TV mounting near me\", \"drywall repair\", \"fixture install\" are higher-intent than \"handyman\"." },
    { heading: "Instant pricing wins the booking", body: "Hourly transparency + flat-rate task pricing close the call before competitors call back." },
    { heading: "Reviews and insurance drive trust", body: "Homeowners letting strangers in want proof — above the fold." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a handyman website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a handyman website?", a: "Typical timeline is 4–6 weeks for a 12–20 page handyman site with full service and service-area pages. Larger multi-location handyman businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs handyman businesses?", a: "No — we build handyman sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Handyman Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for handyman businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/handyman-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
